/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListRemoteMcpServerHeadersRequest,
  ListRemoteMcpServerHeadersSecurity,
} from "../models/operations/listremotemcpserverheaders.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildRemoteMcpServerHeadersQuery,
  prefetchRemoteMcpServerHeaders,
  queryKeyRemoteMcpServerHeaders,
  RemoteMcpServerHeadersQueryData,
} from "./remoteMcpServerHeaders.core.js";
export {
  buildRemoteMcpServerHeadersQuery,
  prefetchRemoteMcpServerHeaders,
  queryKeyRemoteMcpServerHeaders,
  type RemoteMcpServerHeadersQueryData,
};

export type RemoteMcpServerHeadersQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listServerHeaders remoteMcp
 *
 * @remarks
 * List the headers configured for a remote MCP server
 */
export function useRemoteMcpServerHeaders(
  request: ListRemoteMcpServerHeadersRequest,
  security?: ListRemoteMcpServerHeadersSecurity | undefined,
  options?: QueryHookOptions<
    RemoteMcpServerHeadersQueryData,
    RemoteMcpServerHeadersQueryError
  >,
): UseQueryResult<
  RemoteMcpServerHeadersQueryData,
  RemoteMcpServerHeadersQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildRemoteMcpServerHeadersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listServerHeaders remoteMcp
 *
 * @remarks
 * List the headers configured for a remote MCP server
 */
export function useRemoteMcpServerHeadersSuspense(
  request: ListRemoteMcpServerHeadersRequest,
  security?: ListRemoteMcpServerHeadersSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    RemoteMcpServerHeadersQueryData,
    RemoteMcpServerHeadersQueryError
  >,
): UseSuspenseQueryResult<
  RemoteMcpServerHeadersQueryData,
  RemoteMcpServerHeadersQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildRemoteMcpServerHeadersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setRemoteMcpServerHeadersData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      remoteMcpServerId: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: RemoteMcpServerHeadersQueryData,
): RemoteMcpServerHeadersQueryData | undefined {
  const key = queryKeyRemoteMcpServerHeaders(...queryKeyBase);

  return client.setQueryData<RemoteMcpServerHeadersQueryData>(key, data);
}

export function invalidateRemoteMcpServerHeaders(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      remoteMcpServerId: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "remoteMcp",
      "listServerHeaders",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllRemoteMcpServerHeaders(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "remoteMcp", "listServerHeaders"],
  });
}
